import { ReactElement, useCallback } from 'react';
import { createFetchContext } from '../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../components/info/DisplayError';
import Loading from '../../../components/info/Loading';
import { StudentsRemovalRequestViewResponse } from '../types';
import { getStudentRemovalRequestView } from '../api';
import { StudentRemovalRequestViewNotFound } from './StudentRemovalRequestViewNotFound';

interface StudentRemovalRequestViewLoaderProps {
  studentRemovalRequestId: number;
  children: (props: {
    data: StudentsRemovalRequestViewResponse;
  }) => ReactElement;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  undefined,
  StudentsRemovalRequestViewResponse
>();

export function useStudentRemovalRequestViewLoader() {
  return useFetch();
}

export function StudentRemovalRequestViewLoader({
  studentRemovalRequestId,
  children,
}: StudentRemovalRequestViewLoaderProps) {
  const request = useCallback(() => {
    return getStudentRemovalRequestView(studentRemovalRequestId);
  }, [studentRemovalRequestId]);

  return (
    <FetchProvider request={request} defaultQuery={undefined} fetchImmediately>
      <FetchConsumer>
        {({ data, loading, error, refresh }) => {
          if (error?.status === 404) {
            return <StudentRemovalRequestViewNotFound />;
          }

          if (error) {
            return (
              <DisplayError
                insideCard
                textBody={error.code}
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          if (loading) {
            return (
              <div className="mx-3">
                <Loading insideCard />
              </div>
            );
          }

          if (!data) {
            return (
              <DisplayError
                insideCard
                textBody="Data no cargada"
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          return children({ data });
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
